import React, { useState, useEffect, useMemo } from 'react';
import { SearchDropdown } from '../../../../../components';
import { Poi, PoiLocation } from '../../../../../types';
import { POI_CONFIG } from '../../../../../constants/app.constants';

interface POISelectorProps {
  onPoiSelect: (option: Poi) => void;
  onPoiLocationSelect: (location: string) => void;
  onPoiBreakerSizeChange: (size: string) => void;
  selectedPoi?: Poi;
  selectedPoiLocation?: string;
  selectedPoiBreakerSize?: string;
  isEnabled: boolean;
  // Redux data props
  poiOptions: Poi[];
  poiLocations: PoiLocation[];
  isLoading: boolean;
  error?: string;
}

export const POISelector: React.FC<POISelectorProps> = ({
  onPoiSelect,
  onPoiLocationSelect,
  onPoiBreakerSizeChange,
  selectedPoi,
  selectedPoiLocation,
  selectedPoiBreakerSize,
  isEnabled,
  poiOptions,
  poiLocations,
  isLoading,
  error
}) => {
  const [showPoiLocation, setShowPoiLocation] = useState(false);
  const [showPoiBreakerSize, setShowPoiBreakerSize] = useState(false);

  // Get locations for the selected POI
  const availableLocations = useMemo(() => {
    if (!selectedPoi) return [];
    return poiLocations.filter(location => location.poi_id === selectedPoi.id);
  }, [selectedPoi, poiLocations]);

  useEffect(() => {
    // Check if we have a valid POI selection (not empty object or null/undefined)
    const hasValidPoi = selectedPoi && selectedPoi.id && selectedPoi.model;
    
    // CASE 2: LINE SIDE TAP or LOAD SIDE TAP were selected - Enable POI Location
    if (hasValidPoi && (selectedPoi.model === POI_CONFIG.LINE_SIDE_TAP || selectedPoi.model === POI_CONFIG.LOAD_SIDE_TAP)) {
      setShowPoiLocation(true);
    } else {
      setShowPoiLocation(false);
      // Clear selections when not applicable
      if (selectedPoiLocation) onPoiLocationSelect('');
    }
    
    // CASE 1: BREAKER TIE-IN MSP or BREAKER TIE-IN SUB were selected - Enable Breaker Size (int)
    if (hasValidPoi && (selectedPoi.model === POI_CONFIG.BREAKER_TIE_IN_MSP || selectedPoi.model === POI_CONFIG.BREAKER_TIE_IN_SUB)) {
      setShowPoiBreakerSize(true);
    } else {
      setShowPoiBreakerSize(false);
      // Clear breaker size when not applicable
      if (selectedPoiBreakerSize) onPoiBreakerSizeChange('');
    }
  }, [selectedPoi, selectedPoiLocation, selectedPoiBreakerSize, onPoiLocationSelect, onPoiBreakerSizeChange]);

  const handlePoiSelect = (option: any) => {
    if (!option) {
      // Handle clear action - clear all POI-related fields
      onPoiSelect(undefined as any); // Clear the POI selection
      onPoiLocationSelect(''); // Clear location
      onPoiBreakerSizeChange(''); // Clear POI breaker size
      return;
    }
    const poi = poiOptions?.find(p => p.id === option.id);
    if (poi) {
      onPoiSelect(poi);
    }
  };

  const poiDropdownOptions = poiOptions?.map(option => ({
    id: option.id,
    label: option.model
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs">Select a task to configure POI details</p>
      </div>
    );
  }

  const handlePoiLocationSelect = (option: any) => {
    if (!option) {
      onPoiLocationSelect('');
      return;
    }
    onPoiLocationSelect(option.id);
  };

  const poiLocationOptions = availableLocations.map(location => ({
    id: location.location_name,
    label: location.location_name
  }));

  return (
    <div className="bg-transparent p-0">
      <div className="space-y-4">
        {/* POI Configuration Layout */}
        <div className="grid grid-cols-12 gap-3 items-start">
          <div className="col-span-12">
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              POI
            </label>
            <SearchDropdown
              options={poiDropdownOptions}
              onSelect={handlePoiSelect}
              placeholder="Choose POI method..."
              isLoading={isLoading}
              error={error}
              className="h-9"
              value={selectedPoi && selectedPoi.id && selectedPoi.model ? { id: selectedPoi.id, label: selectedPoi.model } : null}
              disabled={!isEnabled}
            />
          </div>
        </div>

        {/* Conditional rows */}
        {showPoiLocation && (
          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                POI Location
              </label>
              <SearchDropdown
                options={poiLocationOptions}
                onSelect={handlePoiLocationSelect}
                placeholder="Choose POI location..."
                className="h-9"
                value={selectedPoiLocation ? { id: selectedPoiLocation, label: selectedPoiLocation } : null}
                disabled={!isEnabled}
              />
            </div>
          </div>
        )}

        {showPoiBreakerSize && (
          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Breaker Size (A)
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={selectedPoiBreakerSize || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    onPoiBreakerSizeChange(value);
                  }
                }}
                placeholder="Enter breaker size (A)..."
                className="w-full px-2 py-1.5 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={!isEnabled}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
